import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
  useAIPanel,
} from '../components/ConstareAIPanel';

/**
 * Lesson 1.3 visual preview at /preview/lesson/1.3
 *
 * "Vocabulary That Separates Fluent From Clueless" — 10 foundational terms
 * taught with a write-your-own-definition-first pattern, then reveal
 * definition, analogy, memory hook, and why-it-matters. Two hands-on AI
 * exercises. Lock card is a 10-term flip-card grid. Reference card lists
 * 5 terms queued for later weeks plus 20 deferred terms.
 */

export default function LessonPreviewPage1_3() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <ReferenceCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 1 · Foundations · Lesson 1.3
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Vocabulary That Separates Fluent From Clueless
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 35 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Fluency · Judgment</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">2 hands-on AI exercises</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Two people in a meeting."
      accent="amber"
    >
      <div className="my-2 rounded-lg border border-line bg-surface p-5">
        <div className="space-y-4 text-text">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
              Person A
            </p>
            <p className="mt-1">"We could use AI to help with this."</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
              Person B
            </p>
            <p className="mt-1">
              "Sure, but what's our context window look like? Are we doing RAG,
              or just stuffing the prompt? And what's the inference cost at the
              volume we're projecting?"
            </p>
          </div>
        </div>
        <p className="mt-5 border-t border-line pt-4 text-sm text-muted">
          Person A nods. Pretends to know what any of that meant. Quietly opens
          a new tab to Google "what is RAG."
        </p>
      </div>
      <p>
        In every room you will ever be in, there is a Person A and a Person B.
        This lesson is about becoming Person B. Not by faking it. By actually
        knowing.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time"
      eyebrow="Two minutes. No looking back at Lesson 1.2."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> What is a token?
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> What does the AI do
          with your text before it can "think" about it?
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> Why does AI
          hallucinate?
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        Hold your answers, then keep going. We will use all three terms again.
      </p>
    </Section>
  );
}

// ---------- Teaching ----------

type VocabTerm = {
  n: number;
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
};

const TERMS: VocabTerm[] = [
  {
    n: 1,
    term: 'Token',
    definition:
      'A chunk of text (could be a whole word, part of a word, or punctuation) that an AI uses as its basic unit. Models have a vocabulary of usually 50,000 to 100,000 tokens.',
    analogy:
      'Tokens are to AI what frames are to a movie. The movie feels continuous, but it is actually made of discrete frames played back fast. AI responses feel continuous, but they are built one token at a time.',
    hook: 'Your name is probably one token. "antidisestablishmentarianism" is six. One emoji can be three. Tokens are not words.',
    matters:
      'Every cost conversation, every "why is my prompt too long" debug session, every context window discussion, comes back to tokens. This is the unit of currency in AI.',
  },
  {
    n: 2,
    term: 'Embedding',
    definition:
      "The long list of numbers (usually 1,000 to 4,000 of them) that represents a token's meaning inside the model. Similar concepts end up with similar number lists.",
    analogy:
      'Imagine every word in the dictionary plotted as a dot in a giant 4,000-dimensional space. "Cat" and "kitten" sit close together. "Cat" and "spreadsheet" sit far apart. The embedding is just the coordinates of that dot.',
    hook: 'Words you cannot do math on. Numbers you can. Embeddings are how the AI turns words into something it can do math on.',
    matters:
      'Embeddings power search, recommendations, and the RAG systems you will hear about everywhere. When someone says "we built a vector database," they mean "we stored a bunch of embeddings."',
  },
  {
    n: 3,
    term: 'Hallucination',
    definition:
      'When an AI generates plausible-sounding text that is factually wrong. Not a bug. A direct consequence of how the model works (predicting likely text, not retrieving facts).',
    analogy:
      "A student who didn't read the book but is really good at sounding like they did. They fill in plausible details with confidence and sometimes get caught, but usually don't.",
    hook: 'Remember "The Silver Window" by Eleanor Marchwood from Lesson 1.2. The AI invented an entire novel with characters and themes because that is what the next likely tokens were. It was not lying. It was working as designed.',
    matters:
      'This is the central failure mode of every AI system today. Every audit you do in Lesson 1.4 and beyond exists because of this one fact. The most valuable AI skill is not getting the AI to do impressive things. It is catching it when it hallucinates.',
  },
  {
    n: 4,
    term: 'Context window',
    definition:
      'The total amount of text (measured in tokens) the AI can consider at once. Includes your prompt, the conversation history, any documents you attached, and the response being generated.',
    analogy:
      "A whiteboard. Once it is full, you have to erase something to add new content. The model's context window is its whiteboard. Big whiteboards are nice but not infinite.",
    hook: "Claude 4 Sonnet's context window is 200,000 tokens. That is about 150,000 words. About one and a half copies of The Great Gatsby. Big, but you can fill it faster than you'd think.",
    matters:
      'When you hear "the AI forgot what we talked about earlier," what really happened is the conversation fell off the back of the context window. When you hear "we cannot fit the whole document," same thing. Context window is the constraint behind half of all real AI complaints.',
  },
  {
    n: 5,
    term: 'Parameters (and weights)',
    definition:
      'The billions of numbers the model learned during training. These numbers are what make the model "smart." When people say "GPT-4 has 1.8 trillion parameters" or "Llama is 70B," they mean the count of these numbers. Parameters and weights are the same thing in practice.',
    analogy:
      'A piano has 88 keys. A simple keyboard. But the patterns of which keys you press, when, and how hard, are what makes the music. Parameters are like the music: not the hardware, but the learned patterns inside it. A bigger model has more "keys" to work with.',
    hook: 'B = billion. 7B is a small model. 70B is medium. 400B+ is frontier. The number is roughly (but not exactly) proportional to how capable the model is.',
    matters:
      'When you read AI news, model sizes are the headline number. They roughly predict capability, cost, and speed. A 70B model is smarter than a 7B model but slower and more expensive to run.',
  },
  {
    n: 6,
    term: 'Temperature',
    definition:
      'A setting that controls how much randomness the AI uses when picking the next token. Temperature 0 means it always picks the most likely token (deterministic, repetitive). Temperature 1 means it picks more freely (creative, varied). Higher temperatures get weirder.',
    analogy:
      'A dial on a creativity-versus-consistency knob. Low temperature: a careful accountant who always gives the same answer. High temperature: a jazz musician who improvises every time.',
    hook: 'Ask the AI to "write a poem about a cat" at temperature 0 versus temperature 1. Same prompt. Wildly different output.',
    matters:
      'When you want reliability (extracting data, classifying tickets, answering FAQs), use low temperature. When you want variety (brainstorming, creative writing, generating options), use high temperature. This is one of the few dials you can actually turn yourself, and most people never touch it.',
  },
  {
    n: 7,
    term: 'Inference',
    definition:
      'The process of using a trained model to generate a response. Distinct from training, which is how the model learned in the first place. Every time you send a prompt, you trigger inference.',
    analogy:
      'Training is studying for the test (slow, expensive, done once). Inference is taking the test (fast, cheap, done over and over).',
    hook: 'When someone says "inference is expensive at scale," they mean answering millions of user prompts per day costs real money. When you read about a company\'s "inference costs," that is what they are talking about.',
    matters:
      'Training a frontier model costs hundreds of millions of dollars but only happens occasionally. Inference happens every time a user does anything. Most of what AI companies actually pay for, day to day, is inference.',
  },
  {
    n: 8,
    term: 'Prompt',
    definition:
      'The text you send to the AI. Includes your question, any context you provide, and any instructions about how to respond.',
    analogy:
      'A prompt is to AI what a search query is to Google, except infinitely more flexible and infinitely more sensitive to phrasing. Same question, two phrasings, two different answers.',
    hook: '"Write me a poem" gets you a generic poem. "Write me a poem about my dog Bailey, who passed last Tuesday, in the style of Mary Oliver, four stanzas, no rhyming" gets you something useful. The difference is the prompt.',
    matters:
      'Prompt quality is the single biggest input you control. The whole field of "prompt engineering" exists because the same model with a better prompt produces wildly better output.',
  },
  {
    n: 9,
    term: 'RAG (Retrieval-Augmented Generation)',
    definition:
      "A technique where the AI first looks up relevant information from a database or document store, then uses that information to generate its response. Combines AI's writing skill with the precision of search.",
    analogy:
      "Open-book exam versus closed-book exam. RAG is open-book. The AI gets to look at relevant pages before writing its answer. This dramatically reduces hallucination on topics the AI didn't memorize.",
    hook: 'When you ask ChatGPT a question and it cites sources from the web, that is RAG. When a customer support bot finds the right help article and quotes it back to you, that is RAG. When a coding assistant pulls from your codebase, that is RAG.',
    matters:
      'Most useful AI products today are RAG systems. It is the single most important pattern for making AI factually reliable on specific content (your company docs, your codebase, a legal database). When someone says "we are building an AI for X," they almost always mean "we are building a RAG system for X."',
  },
  {
    n: 10,
    term: 'Agents',
    definition:
      'An AI system that can take multiple steps on its own to accomplish a goal, often using tools (web browsers, code interpreters, APIs) to interact with the world. Distinct from a chatbot, which just talks back to you.',
    analogy:
      'A chatbot is a help desk operator. An agent is an intern you can hand a project to. The chatbot answers questions. The agent goes off, does things, comes back with results.',
    hook: '"Book me a flight to Dallas next Tuesday under $400" is an agent task. The AI has to search flights, compare prices, check dates, maybe ask you a clarifying question, then book. Multiple steps. Tools. A goal.',
    matters:
      'Agents are the direction the entire field is moving. By the time you finish this program, "agentic" workflows will be the default mental model for what AI does. Chatbots will feel quaint.',
  },
];

function Teaching() {
  const [revealed, setRevealed] = useState<Set<number>>(new Set());

  const toggle = (n: number) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  };

  return (
    <Section
      id="sec-teaching"
      label="The teaching"
      eyebrow="10 terms. Write your own definition first. Then reveal."
    >
      <p>
        Here is how this lesson works. For each term, you get six things: the
        term, your own first guess at a definition, the actual definition, an
        analogy, a memory hook, and where it shows up in real conversations.
      </p>
      <p>
        Writing your own definition first is not optional. Research on something
        called the <Vocab>generation effect</Vocab> shows that struggling to
        produce an answer, even a wrong one, makes the correct answer 30 to 50%
        more memorable than just reading it. Skip the writing and you lose half
        this lesson.
      </p>
      <p className="text-sm text-muted">
        Use the input on each card. Nothing is saved. Nothing is graded. You
        are doing this for yourself.
      </p>

      <div className="mt-6 flex items-center justify-between rounded-md border border-line bg-surface px-4 py-2.5">
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
          Cards revealed
        </p>
        <div className="flex items-center gap-3">
          <div className="h-1.5 w-32 overflow-hidden rounded-full bg-elevated">
            <div
              className="h-full bg-amber transition-all duration-300"
              style={{ width: `${(revealed.size / TERMS.length) * 100}%` }}
            />
          </div>
          <span className="font-mono text-xs text-text">
            {revealed.size} / {TERMS.length}
          </span>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {TERMS.map((t) => (
          <VocabCard
            key={t.n}
            term={t}
            revealed={revealed.has(t.n)}
            onToggle={() => toggle(t.n)}
          />
        ))}
      </div>

      <Exercise1 />
      <Exercise2 />
    </Section>
  );
}

function VocabCard({
  term,
  revealed,
  onToggle,
}: {
  term: VocabTerm;
  revealed: boolean;
  onToggle: () => void;
}) {
  const [draft, setDraft] = useState('');
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      <div className="flex items-center justify-between border-b border-line bg-elevated px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Term {String(term.n).padStart(2, '0')}
          </span>
          <span className="text-base font-semibold text-text">{term.term}</span>
        </div>
        <button
          onClick={onToggle}
          className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
            revealed
              ? 'border border-line text-muted hover:text-text'
              : 'bg-amber text-ink hover:bg-amber-hover'
          }`}
        >
          {revealed ? 'Hide answer' : 'Reveal answer'}
        </button>
      </div>
      <div className="px-5 py-4">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Your turn · write your own definition first
          </span>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`In one sentence, what is "${term.term}"?`}
            rows={2}
            className="mt-2 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/70 focus:border-amber focus:outline-none"
          />
        </label>

        {revealed && (
          <div className="mt-4 space-y-3 border-t border-line pt-4 text-sm">
            <Block label="Actual definition" body={term.definition} />
            <Block label="Analogy" body={term.analogy} accent />
            <Block label="Memory hook" body={term.hook} />
            <Block label="Why it matters" body={term.matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = false,
}: {
  label: string;
  body: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-md border px-3 py-2.5 ${
        accent
          ? 'border-amber/30 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1 text-text/90">{body}</p>
    </div>
  );
}

// ---------- Exercises ----------

function ExerciseShell({
  number,
  title,
  estMinutes,
  type,
  children,
}: {
  number: number;
  title: string;
  estMinutes: number;
  type: 'guided' | 'captured';
  children: React.ReactNode;
}) {
  return (
    <div className="my-8 overflow-hidden rounded-xl border border-amber/40 bg-amber/[0.03]">
      <div className="flex items-center justify-between border-b border-amber/30 bg-amber/[0.05] px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="rounded-md bg-amber px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-ink">
            Exercise {number}
          </span>
          <span className="text-sm font-semibold text-text">{title}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted">
          <span>~{estMinutes} min</span>
          <span className="h-3 w-px bg-amber/30" />
          <span className="capitalize">{type}</span>
        </div>
      </div>
      <div className="space-y-3 px-5 py-4 text-sm text-text/90">{children}</div>
    </div>
  );
}

const EX1_PROMPT = `Without looking up anything, explain in your own words: what is your context window and what is your temperature set to right now?

Then for each of those two things, tell me how confident you are that you actually know it (versus inferred or guessed it).`;

const EX1_MOCK = `Sure, I'll do this honestly.

Context window:
My context window is around 200,000 tokens. That's roughly 150,000 words, enough to hold a medium-length novel.
Confidence: High. This is a published spec of the model I'm running on, and it's the kind of thing trainers tell us about ourselves directly.

Temperature:
I genuinely don't know my current temperature. That setting is controlled by whoever wired up this app, not by me. From the variability in my outputs I'd guess it's set somewhere in the 0.5 to 0.8 range, which is typical for chat. But I don't have direct access to that number.
Confidence: Low. This is an educated guess from the outside, not knowledge.

Honest summary: the context window number I'm sure about. The temperature I'm guessing.`;

function Exercise1() {
  const ai = useAIPanel();
  return (
    <ExerciseShell
      number={1}
      title="Make the AI define itself"
      estMinutes={4}
      type="guided"
    >
      <p>
        You just learned context window and temperature. Now watch the AI
        describe its own. One of these it actually knows. The other it has
        to guess at.
      </p>
      <div className="rounded-md border border-line bg-ink/60 p-3 font-mono text-xs text-text">
        {EX1_PROMPT}
      </div>
      <button
        onClick={() => ai.loadChatPrompt(EX1_PROMPT, EX1_MOCK)}
        className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
      >
        Load prompt into AI panel →
      </button>
      <p>
        Read carefully. Notice which answers feel solid, which feel hand-wavy,
        and which the AI flags as guesses. The honest version of this answer
        is one of the most useful skills you can train an AI to give you.
      </p>
    </ExerciseShell>
  );
}

const EX2_ZEROSHOT_PROMPT = `Classify this customer email as either "Complaint", "Question", or "Compliment":

"Hey, just wanted to say your new feature is amazing. My team has been waiting for this for months."`;

const EX2_ZEROSHOT_MOCK = `Compliment.

The email expresses positive sentiment about a new feature and mentions the team has been looking forward to it. There's no question being asked and no complaint being raised, so Compliment is the right classification.`;

const EX2_FEWSHOT_PROMPT = `Classify customer emails as Complaint, Question, or Compliment.

Email: "I cannot believe this is still broken after 3 weeks."
Classification: Complaint

Email: "How do I export my data?"
Classification: Question

Email: "Thanks for the fast support last night, saved my launch."
Classification: Compliment

Email: "Hey, just wanted to say your new feature is amazing. My team has been waiting for this for months."
Classification:`;

const EX2_FEWSHOT_MOCK = `Compliment`;

function Exercise2() {
  const ai = useAIPanel();
  return (
    <ExerciseShell
      number={2}
      title="Same question, two prompts"
      estMinutes={5}
      type="guided"
    >
      <p>
        You just learned that a prompt is everything you send to the model.
        Here is how much the shape of your prompt changes the shape of the
        answer. We will classify the same email two ways: once with no
        examples, once with three examples in the prompt.
      </p>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-md border border-line bg-ink/60 p-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            No examples
          </p>
          <p className="mt-1.5 text-xs text-muted">
            Just ask. No examples in the prompt. Wait for whatever shape the
            model decides to return.
          </p>
          <button
            onClick={() => ai.loadChatPrompt(EX2_ZEROSHOT_PROMPT, EX2_ZEROSHOT_MOCK)}
            className="mt-3 w-full rounded-md border border-line bg-elevated px-3 py-2 text-xs font-semibold text-text hover:border-amber hover:text-amber"
          >
            Run with no examples →
          </button>
        </div>
        <div className="rounded-md border border-amber/40 bg-amber/[0.04] p-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
            With examples
          </p>
          <p className="mt-1.5 text-xs text-muted">
            Three example pairs in the prompt, then the new email. The model
            picks up the format from the examples.
          </p>
          <button
            onClick={() => ai.loadChatPrompt(EX2_FEWSHOT_PROMPT, EX2_FEWSHOT_MOCK)}
            className="mt-3 w-full rounded-md bg-amber px-3 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
          >
            Run with examples →
          </button>
        </div>
      </div>

      <p>
        Run both. Notice two things. First, both answers are right. Second,
        the no-examples version explains itself in a paragraph. The
        with-examples version returns one word, matching the format of the
        examples.
      </p>
      <p>
        Now imagine doing this on 10,000 emails. The with-examples output is
        what a real classification pipeline needs: one word, parseable,
        consistent. The no-examples output is what most people accidentally
        build. The difference between 70% accuracy and 95% accuracy at scale
        is almost always a few examples in the prompt. (There is a name for
        this pattern. You will meet it in Week 3.)
      </p>
    </ExerciseShell>
  );
}

// ---------- Your turn ----------

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn"
      eyebrow="Five minutes. No AI. No looking back."
      accent="amber"
    >
      <div className="rounded-lg border border-amber/30 bg-amber/[0.03] p-6">
        <p className="text-text">
          Write the definition, in one sentence each, for these six terms.
          Then check yourself against the cards above. Mark any you got wrong
          or partially wrong and write the right definition out by hand.
        </p>
        <ol className="mt-5 space-y-5">
          <Prompt n={1} q="Token" />
          <Prompt n={2} q="Hallucination" />
          <Prompt n={3} q="Context window" />
          <Prompt n={4} q="Prompt" />
          <Prompt n={5} q="RAG" />
          <Prompt n={6} q="Inference" />
        </ol>
        <p className="mt-5 text-sm text-muted">
          The act of writing the wrong answer, finding out it is wrong, and
          rewriting the right one is doing more for your retention than
          reading the cards three times.
        </p>
      </div>
    </Section>
  );
}

function Prompt({ n, q }: { n: number; q: string }) {
  const [val, setVal] = useState('');
  return (
    <li>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          {String(n).padStart(2, '0')}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          <textarea
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="Your definition (not saved, not graded)…"
            rows={2}
            className="mt-3 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/70 focus:border-amber focus:outline-none"
          />
        </div>
      </div>
    </li>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const questions = [
    {
      q: 'What is the difference between a parameter and a token?',
      a: 'Parameters are the billions of numbers learned during training (the model brain). Tokens are the chunks of text the model processes (the model input and output). Parameters live inside the model permanently. Tokens flow through it.',
    },
    {
      q: 'What is the difference between RAG and fine-tuning?',
      a: 'RAG looks up relevant info at the moment of inference and feeds it into the prompt. Fine-tuning bakes new patterns into the model itself by training it further. RAG is open-book testing. Fine-tuning is going back to school.',
    },
    {
      q: 'What is the difference between an agent and a chatbot?',
      a: 'A chatbot responds to one message at a time. An agent takes multi-step actions toward a goal, often using tools. Chatbots talk. Agents do.',
    },
    {
      q: 'Why does turning down temperature usually help with classification tasks?',
      a: 'Classification has one right answer, and you want the same prompt to produce the same answer every time. Lower temperature means the model picks the most likely token more reliably, so output is consistent across runs and easier to parse downstream.',
    },
  ];
  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Answer in your head, then reveal."
      accent="rose"
    >
      <p className="text-sm text-muted">
        Self-graded. No record. The point is to find what did not stick.
      </p>
      <div className="mt-5 space-y-3">
        {questions.map((q, i) => (
          <QuickCheckItem key={i} n={i + 1} {...q} />
        ))}
      </div>
    </Section>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Come back to it in 6 months."
      accent="amber"
    >
      <div className="overflow-hidden rounded-xl border border-amber/30 bg-gradient-to-br from-elevated to-surface" data-lockcard-body>
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
              Lesson 1.3 · Lock card
            </p>
            <button className="text-xs text-muted hover:text-text">
              ⤓ Save image
            </button>
          </div>
          <p className="mt-4 text-lg leading-relaxed text-text">
            Ten terms separate fluent from clueless at the foundation level.
            Knowing them is the difference between participating in real AI
            conversations and pretending to.
          </p>
          <p className="mt-3 text-base text-muted">
            Tap any card to flip from term to analogy. The analogy is the
            handle. The handle is what makes the word stick.
          </p>
          <div className="mt-6">
            <FlipCardGrid />
          </div>
        </div>
      </div>
    </Section>
  );
}

function FlipCardGrid() {
  const [flipped, setFlipped] = useState<Set<number>>(new Set());

  const toggle = (n: number) => {
    setFlipped((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  };

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {TERMS.map((t) => {
        const isFlipped = flipped.has(t.n);
        return (
          <button
            key={t.n}
            onClick={() => toggle(t.n)}
            className={`group relative min-h-[110px] rounded-lg border p-3 text-left transition-colors ${
              isFlipped
                ? 'border-amber/50 bg-amber/[0.05]'
                : 'border-line bg-ink/60 hover:border-amber/40'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[9px] uppercase tracking-widest text-amber">
                {String(t.n).padStart(2, '0')}
              </span>
              <span className="font-mono text-[9px] text-muted/70 group-hover:text-amber">
                {isFlipped ? 'definition' : 'analogy'} ↻
              </span>
            </div>
            {isFlipped ? (
              <p className="mt-1 text-xs leading-snug text-text/90">
                {t.analogy.length > 120
                  ? t.analogy.slice(0, 120).trim() + '…'
                  : t.analogy}
              </p>
            ) : (
              <div className="mt-1">
                <p className="text-sm font-semibold text-text">{t.term}</p>
                <p className="mt-1 text-[11px] leading-snug text-muted">
                  {(() => {
                    const first = t.definition.split('.')[0].trim();
                    return first.length > 80 ? first.slice(0, 80).trim() + '…' : first + '.';
                  })()}
                </p>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------- Reference card ----------

function ReferenceCard() {
  const [open, setOpen] = useState(false);
  const groups: { label: string; items: { term: string; gloss: string }[] }[] = [
    {
      label: 'Coming in later weeks',
      items: [
        { term: 'Training cutoff', gloss: 'The date after which the model has not seen any data. We teach it next, in 1.4, because cutoffs are the top source of confidently wrong answers.' },
        { term: 'System prompt', gloss: 'A hidden instruction that sets the AI\'s role, tone, and rules before your conversation starts. We teach it in Week 3 when you start engineering prompts.' },
        { term: 'Few-shot', gloss: 'Showing the AI a few examples of the pattern you want, then asking for a new one. We teach it in Week 3 as a working technique, not a flashcard.' },
        { term: 'Fine-tuning', gloss: 'Training a model further on your specific data to make it better at a specific task. We teach it in Week 4 next to its alternatives.' },
        { term: 'API', gloss: 'The technical interface that lets software talk to AI models. We teach it in Week 3-4 when you start wiring real tools together.' },
      ],
    },
    {
      label: 'Behavior and quality',
      items: [
        { term: 'Top-p', gloss: 'Another randomness setting, alternative to temperature.' },
        { term: 'Latency', gloss: 'How long the AI takes to respond.' },
        { term: 'Grounding', gloss: 'Tying AI responses to specific source documents (the opposite of hallucinating).' },
        { term: 'Reasoning models', gloss: 'Newer AI models that "think" longer before answering.' },
        { term: 'Chain-of-thought', gloss: 'Asking the AI to show its reasoning step by step.' },
        { term: 'Benchmark', gloss: 'Standard tests used to compare model performance.' },
        { term: 'Evals', gloss: 'Custom tests you run to check if AI is doing what you want.' },
      ],
    },
    {
      label: 'Prompting techniques',
      items: [
        { term: 'Zero-shot', gloss: 'Asking the AI without any examples.' },
        { term: 'Role prompting', gloss: 'Telling the AI to "act as" a specific persona.' },
        { term: 'Context stuffing', gloss: 'Cramming lots of background info into the prompt.' },
      ],
    },
    {
      label: 'Beyond chat',
      items: [
        { term: 'Tool use', gloss: 'When AI calls external functions or services to get things done.' },
        { term: 'Function calling', gloss: 'The technical pattern that enables tool use.' },
        { term: 'MCP', gloss: 'Model Context Protocol — a new standard for connecting AI to your apps and data.' },
        { term: 'Multi-agent', gloss: 'Multiple AI agents working together on the same task.' },
      ],
    },
    {
      label: 'Safety and limits',
      items: [
        { term: 'Jailbreak', gloss: 'Tricking an AI into ignoring its safety rules.' },
        { term: 'Guardrails', gloss: 'Software that blocks unsafe or off-policy AI behavior.' },
        { term: 'Alignment', gloss: 'The field of making AI behave the way humans want it to.' },
      ],
    },
    {
      label: 'Cost and infrastructure',
      items: [
        { term: 'Input vs output tokens', gloss: 'Different pricing for what you send vs what the AI generates.' },
        { term: 'Rate limits', gloss: 'The cap on how many requests you can send per minute.' },
        { term: 'Model size', gloss: 'Measured in parameters. Rough proxy for capability.' },
      ],
    },
  ];

  return (
    <section className="mt-12 scroll-mt-24 rounded-xl border border-line bg-surface">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left hover:bg-elevated/40"
      >
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Reference card
          </p>
          <h3 className="mt-1 text-lg font-semibold text-text">
            25 more terms you will meet later
          </h3>
          <p className="mt-1 text-sm text-muted">
            These are real terms you will hear in AI conversations. We will
            teach each one properly the first time it shows up.
          </p>
        </div>
        <span className="shrink-0 rounded-md border border-line px-3 py-1.5 text-xs text-muted">
          {open ? 'Hide' : 'Show'} reference
        </span>
      </button>
      {open && (
        <div className="space-y-6 border-t border-line px-6 py-6">
          {groups.map((g) => (
            <div key={g.label}>
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
                {g.label}
              </p>
              <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                {g.items.map((it) => (
                  <li
                    key={it.term}
                    className="rounded-md border border-line bg-ink/40 px-3 py-2"
                  >
                    <span className="text-sm font-semibold text-text">
                      {it.term}
                    </span>
                    <span className="ml-2 text-sm text-muted">— {it.gloss}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="text-sm text-muted">
            Keep this card. When one of these terms shows up in a later lesson,
            you will already have the seed for it.
          </p>
        </div>
      )}
    </section>
  );
}

// ---------- Shared primitives ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 1.4
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">Your First Audit</h3>
          <p className="mt-2 text-sm text-muted">
            You have the vocabulary and the mechanics. Time to put them to
            work. We take real AI output and tear it apart for accuracy,
            source quality, and reasoning. By the end you will have a
            repeatable audit process you can use on any AI response.
          </p>
        </div>
        <button className="shrink-0 rounded-md bg-amber px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-hover">
          Start →
        </button>
      </div>
    </div>
  );
}

function Vocab({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-b border-dotted border-amber/60 text-text">
      {children}
    </span>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: 'From last time' },
    { id: 'sec-teaching', label: 'Teaching' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
